/**
 * Cashflow forecast — обязательства и ожидаемые поступления на 90 дней.
 *
 * Outflows: налоги по due_date, ЗП-выплаты по approved-ведомостям.
 * Inflows: ожидаемые доплаты по объектам с открытой AR (по planned_end или signed+60).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Spinner } from "@/components/Spinner";
import { Icon } from "@/components/prototype/Icon";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import {
  fetchCashflowForecast, formatRub,
  type CashflowItem, type CashflowItemType,
} from "@/api/finance";
import { ApiError } from "@/api/client";


const TYPE_TONE: Record<CashflowItemType, { bg: string; fg: string; border: string; icon: string }> = {
  "tax":         { bg: "var(--brick-50)",   fg: "var(--brick-deep)", border: "var(--brick-100)",        icon: "report" },
  "payroll":     { bg: "rgba(212,167,58,0.15)", fg: "#8a6e10",       border: "rgba(212,167,58,0.4)",    icon: "users"  },
  "ar-expected": { bg: "var(--green-soft)", fg: "#1F4F1F",           border: "rgba(61,122,61,0.3)",     icon: "money"  },
  "other":       { bg: "var(--paper-2)",    fg: "var(--mute)",       border: "var(--line)",             icon: "info"   },
};


function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}


function groupByMonth(items: CashflowItem[]): Map<string, CashflowItem[]> {
  const m = new Map<string, CashflowItem[]>();
  for (const it of items) {
    const ym = it.date.slice(0, 7);
    if (!m.has(ym)) m.set(ym, []);
    m.get(ym)!.push(it);
  }
  return m;
}

function fmtMonthTitle(ym: string): string {
  const months = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
  const [y, m] = ym.split("-");
  return `${months[parseInt(m, 10) - 1]} ${y}`;
}


export function FinanceForecastPage() {
  const [days, setDays] = useState<number>(90);
  const [filter, setFilter] = useState<"" | "out" | "in">("");

  const q = useQuery({
    queryKey: ["cashflow-forecast", days],
    queryFn: () => fetchCashflowForecast({ days }),
  });

  const items = useMemo(() => {
    const all = q.data?.items || [];
    if (filter === "out") return all.filter(i => i.is_outflow);
    if (filter === "in") return all.filter(i => !i.is_outflow);
    return all;
  }, [q.data, filter]);

  if (q.isLoading) return <FinanceLayout><Spinner size="lg" label="Расчёт прогноза…" /></FinanceLayout>;
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return <FinanceLayout><div className="alert alert-amber"><div className="alert-body">Только для админа</div></div></FinanceLayout>;
    }
    return <FinanceLayout><div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div></FinanceLayout>;
  }

  const data = q.data!;
  const grouped = groupByMonth(items);
  const months = Array.from(grouped.keys()).sort();

  // Топ-критичные платежи (ближайшие 30 дней, outflow)
  const next30 = data.items.filter(i => {
    const days = Math.floor((Date.parse(i.date) - Date.now()) / 86400000);
    return days >= 0 && days <= 30 && i.is_outflow;
  });
  const next30Sum = next30.reduce((a, i) => a + i.amount, 0);

  return (
    <FinanceLayout>
      <div className="hero-overview" style={{ marginBottom: 20 }}>
        <div>
          <div className="hero-headline" style={{ color: data.totals.net >= 0 ? "#fff" : "#FFAA88" }}>
            {data.totals.net >= 0 ? "+" : ""}{formatRub(data.totals.net, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
            Чистый кэш-фло на {data.horizon_days} дней (до {fmtDate(data.horizon_end)}).<br />
            {data.totals.items_count} запланированных операций.
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">К уплате</div>
          <div className="hero-stat-value" style={{ color: data.totals.outflow > 0 ? "#FF6B6B" : "#fff" }}>
            {formatRub(data.totals.outflow, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>
            В ближайшие 30 дней: {formatRub(next30Sum, { unit: true })}
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">Ожидаем получить</div>
          <div className="hero-stat-value" style={{ color: data.totals.inflow > 0 ? "#82E682" : "#fff" }}>
            {formatRub(data.totals.inflow, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>
            По объектам с открытой AR (план-датам)
          </div>
        </div>
      </div>

      <div className="card card-tight" style={{ marginBottom: 16, padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div className="chip-row" style={{ marginBottom: 0 }}>
            <span style={{ fontSize: 12, color: "var(--mute)", marginRight: 4, alignSelf: "center" }}>Горизонт:</span>
            {[30, 60, 90, 180].map(d => (
              <button key={d} className={`chip ${days === d ? "active" : ""}`} onClick={() => setDays(d)}>
                {d} дн
              </button>
            ))}
          </div>
          <div className="chip-row" style={{ marginBottom: 0, marginLeft: 12 }}>
            <span style={{ fontSize: 12, color: "var(--mute)", marginRight: 4, alignSelf: "center" }}>Тип:</span>
            <button className={`chip ${filter === "" ? "active" : ""}`} onClick={() => setFilter("")}>Все</button>
            <button className={`chip ${filter === "out" ? "active" : ""}`} onClick={() => setFilter("out")}>К уплате</button>
            <button className={`chip ${filter === "in" ? "active" : ""}`} onClick={() => setFilter("in")}>Поступления</button>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--mute)", padding: 32 }}>
          На горизонт {data.horizon_days} дней нет запланированных операций.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {months.map(ym => {
            const monthItems = grouped.get(ym) || [];
            const monthOut = monthItems.filter(i => i.is_outflow).reduce((a, i) => a + i.amount, 0);
            const monthIn = monthItems.filter(i => !i.is_outflow).reduce((a, i) => a + i.amount, 0);
            const monthNet = monthIn - monthOut;
            return (
              <div key={ym} className="card" style={{ padding: 0 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 16px", background: "var(--paper-2)",
                  borderBottom: "1px solid var(--line-soft)",
                }}>
                  <span style={{ fontFamily: "var(--serif)", fontWeight: 700, fontSize: 15 }}>
                    {fmtMonthTitle(ym)}
                  </span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 12, color: "var(--brick)" }}>− {formatRub(monthOut, { unit: true })}</span>
                  <span style={{ fontSize: 12, color: "var(--green)" }}>+ {formatRub(monthIn, { unit: true })}</span>
                  <span style={{
                    fontFamily: "var(--serif)", fontWeight: 700, fontSize: 14,
                    color: monthNet >= 0 ? "var(--green)" : "var(--brick)",
                  }}>
                    = {monthNet >= 0 ? "+" : ""}{formatRub(monthNet, { unit: true })}
                  </span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <tbody>
                    {monthItems.map((it, idx) => {
                      const tone = TYPE_TONE[it.type];
                      return (
                        <tr key={`${it.type}-${it.ref_id}-${idx}`}
                            style={{ borderBottom: "1px solid var(--line-soft)" }}
                            className="ar-row">
                          <td style={{ padding: "10px 16px", whiteSpace: "nowrap", color: "var(--ink-3)", width: 110 }}>
                            {fmtDate(it.date)}
                          </td>
                          <td style={{ padding: "10px 8px", width: 24 }}>
                            <span title={it.category} style={{ display: "inline-flex" }}><Icon name={tone.icon} size={14}/></span>
                          </td>
                          <td style={{ padding: "10px 8px" }}>
                            <div style={{ fontWeight: 600 }}>{it.label}</div>
                            <div style={{ fontSize: 11, color: "var(--mute)" }}>
                              {it.category}
                              {it.status === "draft" && <> · <span style={{ color: "var(--mute)" }}>черновик</span></>}
                              {it.status === "expected" && <> · <span style={{ color: "var(--mute)" }}>прогноз</span></>}
                            </div>
                          </td>
                          <td style={{
                            padding: "10px 16px", textAlign: "right", whiteSpace: "nowrap",
                            fontFamily: "var(--serif)", fontWeight: 700, fontSize: 15,
                            color: it.is_outflow ? "var(--brick)" : "var(--green)",
                          }}>
                            {it.is_outflow ? "−" : "+"}{formatRub(it.amount, { unit: true })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 16, padding: 12, background: "var(--paper-2)", borderRadius: 8, fontSize: 12, color: "var(--mute)" }}>
        Прогноз построен на основе: <b>tax_returns</b> (статусы draft/approved с сроком уплаты),
        <b> payroll_accruals</b> (approved с balance &gt; 0), <b>production_objects</b> (открытая AR — дата planned_end
        или signed+60 дней). Регулярные расходы (аренда, маркетинг, аутсорс) НЕ учитываются — добавь их вручную как
        будущие expenses, если нужны.
      </div>

      <style>{`
        .ar-row > td { transition: background 100ms ease; }
        .ar-row:hover > td { background: var(--paper-2); }
      `}</style>
    </FinanceLayout>
  );
}
